import { useNetworkHistory, useNetworkOverview } from '../../../features/locations/use-network-overview'
import { useIdOrCode } from '../../use-id-or-code'
import { Badge, Card, Crumb, cardCls, LinkButton, mono, Notice, PageHeader, SkeletonCards, Stat } from './ui'

const th = 'h-10 px-5 text-left text-[12px] font-medium uppercase tracking-[0.04em] text-[var(--color-muted)]'
const td = 'h-11 border-t border-[var(--color-border)] px-5'

export function PorcelainNetwork() {
  const idOrCode = useIdOrCode()
  const { data, isLoading, error } = useNetworkOverview(idOrCode)
  const history = useNetworkHistory(idOrCode)

  const header = (
    <PageHeader
      eyebrow={<Crumb idOrCode={idOrCode} trail="network" />}
      title="Network"
      subtitle="Live device and tunnel status for this store."
      right={
        <LinkButton to={`/locations/${encodeURIComponent(idOrCode)}/network/wan-settings`}>WAN settings</LinkButton>
      }
    />
  )

  if (isLoading)
    return (
      <section>
        {header}
        <SkeletonCards rows={3} />
      </section>
    )
  if (error || !data)
    return (
      <section>
        {header}
        <Notice
          tone="error"
          title="We couldn't load network status"
          body="Check your connection and try again in a moment."
        />
      </section>
    )

  const devices = Object.entries(data.status.data)
  const wans = data.status.wanInterfaces ?? []
  const { tunnel, summary } = data

  return (
    <section>
      {header}

      <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
        <Stat label="Devices" value={summary.total} />
        <Stat label="Up" value={summary.up} tone="ok" />
        <Stat label="Down" value={summary.down} tone={summary.down > 0 ? 'bad' : undefined} />
        <Stat label="Other" value={summary.other} />
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-[1.4fr_1fr]">
        <Card
          title="Devices"
          aside={data.status.locationStatus ? <Badge label={data.status.locationStatus} /> : undefined}
        >
          {devices.length === 0 ? (
            <p className="px-5 py-10 text-center text-[13px] text-[var(--color-muted)]">No devices reported yet.</p>
          ) : (
            <table className="w-full border-collapse text-[14px]">
              <thead>
                <tr>
                  <th className={th}>Device</th>
                  <th className={`${th} w-[120px]`}>Status</th>
                </tr>
              </thead>
              <tbody>
                {devices.map(([name, status]) => (
                  <tr key={name} className="transition-colors hover:bg-[var(--v1-accent-soft)]">
                    <td className={`${td} font-medium`} style={mono}>
                      {name}
                    </td>
                    <td className={td}>
                      <Badge label={status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <div className="grid content-start gap-5">
          <Card title="Tunnel">
            {tunnel ? (
              <dl>
                {(
                  [
                    ['Tunnel', <Badge key="t" label={tunnel.tunnelStatus} />],
                    ['Device', tunnel.deviceStatus ? <Badge key="d" label={tunnel.deviceStatus} /> : null],
                    ['VPN mode', tunnel.vpnMode],
                  ] as const
                ).map(([k, v]) => (
                  <div
                    key={k}
                    className="flex h-11 items-center justify-between border-t border-[var(--color-border)] px-5 first:border-t-0"
                  >
                    <dt className="text-[13px] text-[var(--color-muted)]">{k}</dt>
                    <dd className="text-[14px] font-medium">
                      {v ?? <span className="text-[var(--color-muted)]">-</span>}
                    </dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="px-5 py-8 text-center text-[13px] text-[var(--color-muted)]">
                No tunnel data is available for this store.
              </p>
            )}
          </Card>

          <Card title="WAN interfaces">
            {wans.length === 0 ? (
              <p className="px-5 py-8 text-center text-[13px] text-[var(--color-muted)]">No WAN interfaces reported.</p>
            ) : (
              wans.map((w) => (
                <div
                  key={w.interfaceName}
                  className="flex min-h-12 items-center justify-between gap-3 border-t border-[var(--color-border)] px-5 py-2 first:border-t-0"
                >
                  <div>
                    <div className="text-[14px] font-medium" style={mono}>
                      {w.interfaceName}
                    </div>
                    <div className="text-[12px] text-[var(--color-muted)]">
                      {[w.vendor, w.wanType].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <Badge label={w.vendorStatus} />
                </div>
              ))
            )}
          </Card>
        </div>
      </div>

      <div className="mt-5">
        <Card title="History">
          {history.deferred ? (
            <div
              className={`${cardCls} m-5 border-dashed bg-[var(--color-surface-2)] px-6 py-10 text-center`}
              style={{ boxShadow: 'none' }}
            >
              <p className="text-[15px] font-medium">Coming soon</p>
              <p className="mx-auto mt-1 max-w-[420px] text-[13px] text-[var(--color-muted)]">
                Device and tunnel history, VLAN client counts, and drift insights will appear here in an upcoming
                release.
              </p>
            </div>
          ) : null}
        </Card>
      </div>
    </section>
  )
}
